import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { getCountryCallingCode } from "react-phone-number-input";

// The menu is rendered in a portal so modals with `overflow: hidden` (the
// mobile Set/Reset MPIN shell, for one) can't clip it, which means its position
// has to be measured from the toggle instead of inherited from an ancestor.
const MENU_GUTTER = 8;
const MENU_MAX_WIDTH = 320;
const MENU_MAX_HEIGHT = 340;
const MENU_MIN_HEIGHT = 160;

function getCallingCode(country) {
  if (!country) return "";
  try {
    return `+${getCountryCallingCode(country)}`;
  } catch {
    // Countries missing from the "min" metadata bundle have no calling code
    return "";
  }
}

/**
 * Country picker for `react-phone-number-input`. Replaces the library's native
 * `<select/>`, which the browser paints with its own light-theme popup and
 * renders as a full-height, unsearchable list of ~250 countries.
 */
export default function CountrySelectDropdown({
  value,
  onChange,
  options,
  disabled,
  readOnly,
  iconComponent: Icon,
  onFocus,
  onBlur,
  "aria-label": ariaLabel,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuStyle, setMenuStyle] = useState(null);
  const toggleRef = useRef(null);
  const menuRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);

  const isLocked = Boolean(disabled || readOnly);

  const countries = useMemo(
    () =>
      (options || [])
        .filter((option) => !option.divider && option.value)
        .map((option) => ({
          value: option.value,
          label: option.label || option.value,
          callingCode: getCallingCode(option.value),
        })),
    [options]
  );

  const selected = useMemo(
    () => countries.find((country) => country.value === value),
    [countries, value]
  );

  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return countries;
    const digits = term.replace(/\D/g, "");
    return countries.filter(
      (country) =>
        country.label.toLowerCase().includes(term) ||
        country.value.toLowerCase().startsWith(term) ||
        (digits && country.callingCode.includes(digits))
    );
  }, [countries, query]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery("");
  }, []);

  const positionMenu = useCallback(() => {
    const anchor = toggleRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const width = Math.min(MENU_MAX_WIDTH, viewportWidth - MENU_GUTTER * 2);
    // Half the viewport keeps the list short on phones and laptops alike
    const preferredHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.round(viewportHeight * 0.5)
    );
    const spaceBelow = viewportHeight - rect.bottom - MENU_GUTTER * 2;
    const spaceAbove = rect.top - MENU_GUTTER * 2;
    const dropUp = spaceBelow < MENU_MIN_HEIGHT && spaceAbove > spaceBelow;
    const available = dropUp ? spaceAbove : spaceBelow;
    const maxHeight = Math.max(
      MENU_MIN_HEIGHT,
      Math.min(preferredHeight, available)
    );
    const left = Math.min(
      Math.max(MENU_GUTTER, rect.left),
      Math.max(MENU_GUTTER, viewportWidth - width - MENU_GUTTER)
    );

    setMenuStyle({
      width,
      maxHeight,
      left,
      ...(dropUp
        ? { bottom: viewportHeight - rect.top + MENU_GUTTER }
        : { top: rect.bottom + MENU_GUTTER }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    // Scroll is captured from `window` so a scrolling modal body moves the menu
    // with the field, but scrolling the country list itself must not reposition
    // anything — capture-phase listeners see those events too.
    const handleScroll = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      positionMenu();
    };

    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, positionMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (toggleRef.current && toggleRef.current.contains(event.target)) return;
      if (menuRef.current && menuRef.current.contains(event.target)) return;
      close();
    };
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      close();
      if (toggleRef.current) toggleRef.current.focus();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (!isOpen) return;
    // Not on touch devices: focusing raises the on-screen keyboard, which would
    // cover the list the user just opened.
    const isTouch =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches;
    if (!isTouch && searchRef.current) searchRef.current.focus();

    const list = listRef.current;
    const selectedOption = list && list.querySelector('[data-selected="true"]');
    if (!list || !selectedOption) return;
    // Scrolled by hand rather than with `scrollIntoView()`, which would also
    // scroll the page/modal behind the (fixed) menu.
    list.scrollTop =
      selectedOption.offsetTop -
      list.clientHeight / 2 +
      selectedOption.offsetHeight / 2;
  }, [isOpen]);

  const selectCountry = (country) => {
    // The library moves focus to the number input itself, so closing after the
    // change never leaves focus stranded on the unmounted menu.
    onChange(country);
    close();
  };

  const moveFocus = (from, step) => {
    if (!listRef.current) return;
    const items = Array.from(
      listRef.current.querySelectorAll('[role="option"]')
    );
    if (!items.length) return;
    const currentIndex = items.indexOf(from);
    const nextIndex =
      currentIndex === -1
        ? 0
        : Math.min(Math.max(currentIndex + step, 0), items.length - 1);
    items[nextIndex].focus();
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveFocus(null, 0);
      return;
    }
    if (event.key === "Enter" && results.length) {
      event.preventDefault();
      selectCountry(results[0].value);
    }
  };

  const handleOptionKeyDown = (event) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    moveFocus(event.currentTarget, event.key === "ArrowDown" ? 1 : -1);
  };

  return (
    <div className="PhoneInputCountry phone-country">
      <button
        ref={toggleRef}
        type="button"
        className="phone-country__toggle"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={isLocked}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel || "Select country"}
      >
        {Icon ? (
          <Icon
            aria-hidden
            country={value}
            label={selected ? selected.label : ariaLabel}
          />
        ) : null}
        <span
          className="phone-country__arrow PhoneInputCountrySelectArrow"
          aria-hidden="true"
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="phone-country-menu"
            style={
              menuStyle
                ? menuStyle
                : { top: 0, left: 0, visibility: "hidden" }
            }
          >
            <div className="phone-country-menu__search">
              <i className="bi bi-search" aria-hidden="true" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search country or code"
                aria-label="Search country"
                autoComplete="off"
              />
            </div>

            <div
              ref={listRef}
              className="phone-country-menu__list"
              role="listbox"
              aria-label={ariaLabel || "Countries"}
            >
              {results.map((country) => {
                const isSelected = country.value === value;
                return (
                  <button
                    key={country.value}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    className={`phone-country-menu__option${
                      isSelected ? " is-selected" : ""
                    }`}
                    onClick={() => selectCountry(country.value)}
                    onKeyDown={handleOptionKeyDown}
                  >
                    <span className="phone-country-menu__name">
                      {country.label}
                    </span>
                    <span className="phone-country-menu__code">
                      {country.callingCode}
                    </span>
                  </button>
                );
              })}

              {!results.length && (
                <p className="phone-country-menu__empty">No countries found</p>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
